// The carrier catalog is ~1,400 entries and identical for every server render,
// so each pod keeps one copy in process instead of re-fetching and re-parsing it
// per SSR. Built as a factory so the TTL, the clock, and the fetch itself can be
// injected in tests instead of being reached through the module registry.
const DEFAULT_TTL_MS = 5 * 60 * 1000;
// Failures are remembered too, briefly: without this a wapi outage costs a failed
// catalog request on every render, against a service that is already struggling.
// It only bites a cold pod — one with a cached copy serves that and refreshes in
// the background.
const DEFAULT_FAILURE_TTL_MS = 10 * 1000;

/**
 * @param {Object} opts
 * @param {Function} opts.fetchCatalog - returns a Promise of the carrier map, keyed by carrier code
 * @param {Number} [opts.failureTtlMs] - how long a failed fetch suppresses the next attempt
 * @param {Function} [opts.now] - clock, in ms
 * @param {Number} [opts.ttlMs] - how long a fetched catalog stays fresh
 * @param {Function} [opts.onFetchError] - called once per failed fetch, with (error, {servingStale})
 * @return {{load: Function}}
 */
function createCatalogCache(opts) {
    const {
        failureTtlMs = DEFAULT_FAILURE_TTL_MS,
        fetchCatalog,
        now = Date.now,
        onFetchError,
        ttlMs = DEFAULT_TTL_MS,
    } = opts;

    let cached;
    let failure;
    let inFlight;

    function isFresh() {
        return !!cached && cached.expiresAt > now();
    }

    function isCoolingDown() {
        return !!failure && failure.coolsDownAt > now();
    }

    // Stale carrier data renders a correct-looking page; no carrier data renders
    // one with missing names and features. Prefer stale. Reported once by
    // onFetchError when the fetch fails, not on every load that serves stale
    // during the cooldown — an outage would otherwise log per render.
    function staleOrThrow(ex) {
        if (!cached) {
            throw ex;
        }

        return cached.catalog;
    }

    function refresh() {
        if (!inFlight) {
            // One fetch serves every render waiting on it. The promise therefore
            // outlives the request that started it, which is safe for this call
            // and only this kind of call: carriers.json is the same for everyone,
            // and the wapi service it goes through is already a per-process
            // singleton whose visitorId and store are overwritten per request
            // (see packages/wanderu-shellapp/src/entries/server.jsx).
            inFlight = fetchCatalog()
                .then((catalog) => {
                    cached = {
                        catalog,
                        expiresAt: now() + ttlMs,
                    };
                    failure = undefined;

                    return catalog;
                })
                .catch((ex) => {
                    failure = {
                        coolsDownAt: now() + failureTtlMs,
                        error: ex,
                    };

                    if (onFetchError) {
                        onFetchError(ex, { servingStale: !!cached });
                    }

                    throw ex;
                })
                .finally(() => {
                    inFlight = undefined;
                });
        }

        return inFlight;
    }

    async function load() {
        if (isFresh()) {
            return cached.catalog;
        }

        if (isCoolingDown()) {
            return staleOrThrow(failure.error);
        }

        const pending = refresh();

        // Stale-while-revalidate, and the reason the refresh is never awaited
        // once there is anything to serve. serverApiService sets no timeout, so
        // awaiting it would put an unbounded wapi round trip in front of *every*
        // render on the pod, not just the one that started it — and a hung
        // request is the failure a 646 KB response is likeliest to produce. A
        // catalog a few minutes old renders the same page.
        if (cached) {
            // The refresh's own .catch has already recorded and reported the
            // failure; this handler only keeps an unawaited rejection from
            // surfacing as an unhandledRejection.
            pending.catch(() => undefined);

            return cached.catalog;
        }

        // Nothing cached, so this render has no page to give without the catalog
        // and has to wait for the fetch it just started.
        try {
            return await pending;
        } catch (ex) {
            return staleOrThrow(ex);
        }
    }

    return { load };
}

export {
    DEFAULT_FAILURE_TTL_MS,
    DEFAULT_TTL_MS,
    createCatalogCache,
};
